<script setup lang="ts">
/**
 * A repo's branches and the merges between them (`RepoBranchSet`), drawn: a
 * box per branch and one for the Claude worktrees, an arrow per direction a
 * merge may go — solid where it goes on its own, dashed where only by hand —
 * and ↑ on a branch pushed on its own, ↓ on one pulled on its own. Only a picture of the settings: the
 * list beside it (RepoBranches.vue) is where they are edited.
 *
 * Laid out top down by the longest path from the worktrees, so work flows
 * down: worktrees, then staging, then main. A link back up (main into
 * staging) is a back edge, which does not count, so it cannot push main above
 * staging.
 */
import { computed, useId } from "vue";
import { STAGING_BRANCH, TRUNK_NODE, WORKTREES_NODE, type RepoBranchSet } from "@shared/repoSettings.js";

const props = defineProps<{ set: RepoBranchSet }>();

const uid = useId();

const NODE_H = 26;
const ROW_GAP = 46;
const COL_GAP = 22;
const PAD = 8;
const CHAR_W = 7.2;

interface Node {
  id: string;
  label: string;
  push: boolean;
  pull: boolean;
  w: number;
  x: number;
  y: number;
  layer: number;
}

interface Edge {
  from: string;
  into: string;
  auto: boolean;
  /** The other way is a link too: the two are drawn apart. */
  both: boolean;
  d: string;
  title: string;
}

const layout = computed(() => {
  const { branches, merges } = props.set;
  const hasWorktrees = merges.some((m) => m.from === WORKTREES_NODE);
  const ids = [...(hasWorktrees ? [WORKTREES_NODE] : []), ...branches.map((b) => b.name)];
  const out = new Map<string, string[]>(ids.map((id) => [id, []]));
  for (const m of merges) out.get(m.from)?.push(m.into);

  // Back edges, found depth first from the worktrees (then staging, then the
  // rest), are left out of the layering, so a cycle cannot lift a branch.
  const back = new Set<string>();
  const state = new Map<string, "on" | "done">();
  const visit = (id: string): void => {
    state.set(id, "on");
    for (const next of out.get(id) ?? []) {
      const s = state.get(next);
      if (s === "on") back.add(`${id} ${next}`);
      else if (!s) visit(next);
    }
    state.set(id, "done");
  };
  for (const id of [WORKTREES_NODE, STAGING_BRANCH, ...ids]) if (out.has(id) && !state.has(id)) visit(id);

  const layer = new Map<string, number>(ids.map((id) => [id, 0]));
  // Longest path: relax as many times as there are nodes, which a DAG needs at most.
  for (let i = 0; i < ids.length; i++) {
    for (const m of merges) {
      if (back.has(`${m.from} ${m.into}`) || !layer.has(m.from) || !layer.has(m.into)) continue;
      const l = layer.get(m.from)! + 1;
      if (l > layer.get(m.into)!) layer.set(m.into, l);
    }
  }

  const rows: string[][] = [];
  for (const id of ids) (rows[layer.get(id)!] ??= []).push(id);
  const nodes = new Map<string, Node>();
  const widthOf = (label: string, push: boolean, pull = false): number => Math.max(64, (label.length + (push ? 2 : 0) + (pull ? 2 : 0)) * CHAR_W + 20);
  const rowWidths = rows.map((row) =>
    (row ?? []).reduce((sum, id, i) => {
      const b = branches.find((x) => x.name === id);
      const label = id === WORKTREES_NODE ? "worktrees" : id;
      return sum + widthOf(label, !!b?.autoPush, !!b?.autoPull) + (i ? COL_GAP : 0);
    }, 0),
  );
  const width = Math.max(160, ...rowWidths) + PAD * 2;
  rows.forEach((row, r) => {
    let x = (width - rowWidths[r]!) / 2;
    for (const id of row ?? []) {
      const b = branches.find((x) => x.name === id);
      const label = id === WORKTREES_NODE ? "worktrees" : id;
      const push = !!b?.autoPush;
      const pull = !!b?.autoPull;
      const w = widthOf(label, push, pull);
      nodes.set(id, { id, label, push, pull, w, x: x + w / 2, y: PAD + r * (NODE_H + ROW_GAP) + NODE_H / 2, layer: r });
      x += w + COL_GAP;
    }
  });
  const height = PAD * 2 + rows.length * NODE_H + Math.max(0, rows.length - 1) * ROW_GAP;

  const has = (from: string, into: string): boolean => merges.some((m) => m.from === from && m.into === into);
  /** Where the line from a's centre towards b leaves a's box. */
  const rim = (a: Node, dx: number, dy: number): [number, number] => {
    const t = Math.min(dx ? a.w / 2 / Math.abs(dx) : Infinity, dy ? NODE_H / 2 / Math.abs(dy) : Infinity);
    return [a.x + dx * t, a.y + dy * t];
  };
  const edges: Edge[] = [];
  for (const m of merges) {
    const a = nodes.get(m.from);
    const b = nodes.get(m.into);
    if (!a || !b) continue;
    const both = has(m.into, m.from);
    let dx = b.x - a.x;
    let dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    // The two ways of one link sit either side of the line between the boxes.
    const off = both ? 5 : 0;
    const nx = (-dy / len) * off;
    const ny = (dx / len) * off;
    const [x1, y1] = rim(a, dx, dy);
    dx = a.x - b.x;
    dy = a.y - b.y;
    const [x2, y2] = rim(b, dx, dy);
    const from = m.from === WORKTREES_NODE ? "worktrees" : m.from;
    edges.push({
      from: m.from,
      into: m.into,
      auto: !!m.auto,
      both,
      d: `M${(x1 + nx).toFixed(1)},${(y1 + ny).toFixed(1)} L${(x2 + nx).toFixed(1)},${(y2 + ny).toFixed(1)}`,
      title: `${from} → ${m.into}: ${m.auto ? (m.into === TRUNK_NODE ? "deployed on its own" : "merged on its own") : "merged by hand"}`,
    });
  }
  return { nodes: [...nodes.values()], edges, width, height };
});
</script>

<template>
  <svg
    class="branch-graph"
    :viewBox="`0 0 ${layout.width} ${layout.height}`"
    :style="{ maxWidth: `${layout.width}px` }"
    role="img"
    :aria-label="`Merge links: ${layout.edges.map((e) => e.title).join('; ')}`"
  >
    <defs>
      <marker :id="`${uid}-auto`" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" class="branch-graph-head auto" />
      </marker>
      <marker :id="`${uid}-hand`" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" class="branch-graph-head" />
      </marker>
    </defs>
    <path
      v-for="e in layout.edges"
      :key="`${e.from} ${e.into}`"
      :d="e.d"
      class="branch-graph-edge"
      :class="{ auto: e.auto }"
      :marker-end="`url(#${uid}-${e.auto ? 'auto' : 'hand'})`"
    >
      <title>{{ e.title }}</title>
    </path>
    <g v-for="n in layout.nodes" :key="n.id" class="branch-graph-node" :class="{ worktrees: n.id === WORKTREES_NODE }">
      <rect :x="n.x - n.w / 2" :y="n.y - NODE_H / 2" :width="n.w" :height="NODE_H" rx="6" />
      <text :x="n.x" :y="n.y" dominant-baseline="central" text-anchor="middle">
        {{ n.label }}<tspan v-if="n.push" class="branch-graph-push" dx="4">↑<title>Pushed on its own</title></tspan><tspan v-if="n.pull" class="branch-graph-push" dx="4">↓<title>Pulled on its own</title></tspan>
      </text>
    </g>
  </svg>
</template>
